<template>
  <q-dialog
    ref="dialogRef"
    class="col-8"
    @hide="onDialogHide"
    square
  >
    <q-card
      class="q-pa-md-xl q-pa-xs-xs"
      :style="{ width: ($q.screen.gt.md ? '60vw' : $q.screen.gt.xs ? '80vw' : '100vw'), maxWidth: 'unset' }"
    >
      <q-stepper
        v-model="step"
        header-nav
        ref="stepper"
        color="primary"
        animated
        class="full-width"
        vertical
        active-icon="spi-pen"
        done-icon="spi-check"
      >
        <q-step
          :name="1"
          title="Welcome to Stockpulse"
          icon="spi-gear"
          active-icon="spi-pen"
          done-icon="spi-check"
          :done="step > 1"
          :header-nav="step > 1"
        >
          We're excited to have you here. To get started, let's set up your preferences.
          This allows us to display content that is tailored specifically to you and your interests on your homepage.
          <q-stepper-navigation>
            <q-btn
              @click="() => { done1 = true; step = 2 }"
              color="primary"
              label="Continue"
            />
          </q-stepper-navigation>
        </q-step>

        <q-step
          :name="2"
          title="Market preferences"
          icon="spi-globe"
          active-icon="spi-pen"
          done-icon="spi-check"
          :done="step > 2"
          :header-nav="step > 2"
        >
          <div class="flex">
            <UserPreferenceForm
              :step="1"
              :ui-preferences="uiPreferences"
            />
          </div>

          <q-stepper-navigation>
            <q-btn
              @click="() => { done2 = true; step = 3 }"
              color="primary"
              label="Continue"
              unelevated
              square
            />
            <q-btn
              flat
              @click="step = 1"
              color="primary"
              label="Back"
              class="q-ml-sm"
              unelevated
              square
            />
          </q-stepper-navigation>
        </q-step>

        <q-step
          :name="3"
          :done="step > 3"
          title="Financial Assets"
          icon="spi-list-check"
          active-icon="spi-pen"
          done-icon="spi-check"
          :header-nav="step > 3"
        >
          <div class="flex">
            <UserPreferenceForm
              ref="userPreference"
              :step="2"
              :ui-preferences="uiPreferences"
            />
          </div>
          <q-stepper-navigation>
            <q-btn
              @click="() => { done3 = true; step = 4 }"
              color="primary"
              label="Continue"
              unelevated
              square
            />
            <q-btn
              flat
              @click="step = 2"
              color="primary"
              label="Back"
              class="q-ml-sm"
              unelevated
              square
            />
          </q-stepper-navigation>
        </q-step>
        <q-step
          :name="4"
          title="How to start"
          icon="spi-flag-checkered"
          active-icon="spi-pen"
          done-icon="spi-check"
          :header-nav="step > 3"
        >
          <div class="flex">
            <div class="flex col-12 column">
              Thank you for setting up your preferences! You can modify them anytime on the settings page.
              Check out our tutorial videos at the bottom of the startpage to get started.
            </div>
          </div>
          <q-stepper-navigation>
            <q-btn
              color="primary"
              @click="handleSaveUserOpts(false)"
              label="Finish"
              unelevated
              square
            />
            <q-btn
              color="secondary"
              @click="handleSaveUserOpts(true)"
              label="View Tutorials"
              class="q-ml-sm"
              unelevated
              outline
              square
            />
            <q-btn
              flat
              @click="step = 3"
              color="primary"
              label="Back"
              class="q-ml-sm"
              unelevated
              square
            />
          </q-stepper-navigation>
        </q-step>
      </q-stepper>
      <q-inner-loading :showing="loading">
        <q-spinner-dots
          color="primary"
          size="3em"
        />
      </q-inner-loading>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { Ref, ref } from 'vue'
import { QDialog, useDialogPluginComponent } from 'quasar'
import UserPreferenceForm from 'components/user/UserPreferenceForm.vue'
import { UiPreferencesGUI, useInventoryStore } from 'stores/inventory-store'
import { ROUTE_NAME_TUTORIALS } from 'src/router/routes'
import { useRouter } from 'vue-router'

defineEmits([
  ...useDialogPluginComponent.emits
])

defineProps({
  caption: {
    type: String,
    required: false,
    default: undefined
  }
})

const { dialogRef, onDialogHide } = useDialogPluginComponent()
const loading = ref(false)
const router = useRouter()
const inventoryStore = useInventoryStore()

const step = ref(1)
const done1 = ref(false)
const done2 = ref(false)
const done3 = ref(false)
const done4 = ref(false)

const uiPreferences :Ref<UiPreferencesGUI> = ref({
  markets: {
    US: false,
    CA: false,
    EU: false,
    CN: false,
    IN: false,
    REST_AS: false
  },
  titleTypes: {
    company: true,
    forex: true,
    index: true,
    crypto_currency: true,
    raw_material: true,
    fund: true
  },
  countryUnCodes: [],
  countryIsoCodes: [],
  titleTypesList: []
})

const handleSaveUserOpts = async (scrollToTutorials: boolean) : Promise<void> => {
  await inventoryStore.updateUserOptions(inventoryStore.transformGUIPreferencesToUiPreferences(uiPreferences.value))
  done4.value = true
  dialogRef.value?.hide()

  if (scrollToTutorials) {
    await router.push({ name: ROUTE_NAME_TUTORIALS })
  }
}

</script>
